/**
 * Attachment Types - Unified file attachment system
 * Supports photos, documents, signatures attached to any entity in the system
 */

export type AttachmentType = 
  | 'photo' 
  | 'document' 
  | 'signature' 
  | 'invoice' 
  | 'permit' 
  | 'contract' 
  | 'other';

export type ParentEntityType =
  | 'job'
  | 'quote'
  | 'lead'
  | 'contractor'
  | 'permit'
  | 'material'
  | 'credit'
  | 'purchase_order';

export interface Attachment {
  id: string;
  parentType: ParentEntityType;
  parentId: string;
  attachmentType: AttachmentType;
  fileName: string;
  filePath: string;
  mimeType?: string;
  fileSize?: number;
  createdBy: string;
  createdAt: string;
  notes?: string;
}

export interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

/**
 * Maps entity types to their display labels
 */
export const PARENT_TYPE_LABELS: Record<ParentEntityType, string> = {
  job: 'Job',
  quote: 'Quote',
  lead: 'Lead',
  contractor: 'Contractor',
  permit: 'Permit',
  material: 'Material',
  credit: 'Credit',
  purchase_order: 'Purchase Order',
};

/**
 * Maps attachment types to their display labels
 */
export const ATTACHMENT_TYPE_LABELS: Record<AttachmentType, string> = {
  photo: 'Photo',
  document: 'Document',
  signature: 'Signature',
  invoice: 'Invoice',
  permit: 'Permit',
  contract: 'Contract',
  other: 'Other',
};

/**
 * Default accepted file types for each attachment type
 */
export const ACCEPTED_FILE_TYPES: Record<AttachmentType, string[]> = {
  photo: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  signature: ['image/png', 'image/jpeg'],
  invoice: ['application/pdf', 'image/png', 'image/jpeg'],
  permit: ['application/pdf', 'image/png', 'image/jpeg'],
  contract: ['application/pdf'],
  other: ['application/pdf', 'image/png', 'image/jpeg', 'application/msword'],
};

/**
 * Maps parent entity types to their corresponding storage bucket
 */
export const ENTITY_TYPE_TO_BUCKET: Record<ParentEntityType, string> = {
  job: 'job-photos',
  quote: 'job-documents',
  lead: 'job-documents',
  contractor: 'contractor-files',
  permit: 'job-documents',
  material: 'job-photos',
  credit: 'job-documents',
  purchase_order: 'job-documents',
};

/**
 * Check if a MIME type represents an image
 */
export function isImageMimeType(mimeType?: string): boolean {
  return mimeType?.startsWith('image/') ?? false;
}

/**
 * Get file extension from MIME type
 */
export function getExtensionFromMime(mimeType: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/pdf': 'pdf',
  };
  return map[mimeType] || 'bin';
}

/**
 * Format file size to human readable string
 */
export function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
